import { Field } from '@elysian/ui';
import type { OrganisationSettings } from '../../data/source';
import { CURRENCIES } from '../shared/currencies';
import { checkVatNumber, firstYearRange, FREQUENCIES, followsOn, MONTHS, periodCycle, type OrgPatch } from './orgSettings';
import { longDate } from '../invoices/shared';

/** The editable form of the settings: text fields as strings, so a cleared box stays cleared. */
export type OrgForm = Required<Pick<OrgPatch, 'legal_name' | 'entity_type' | 'vat_registered' | 'vat_basis' | 'vat_frequency'
  | 'vat_period_start_month' | 'year_start_month' | 'first_year' | 'base_currency'>> & Record<
  'trading_name' | 'registered_number' | 'registered_office' | 'address' | 'contact_email' | 'contact_phone' | 'website' | 'vat_number', string>;

export function formFrom(s: OrganisationSettings): OrgForm {
  return {
    legal_name: s.legal_name, trading_name: s.trading_name ?? '', entity_type: s.entity_type,
    registered_number: s.registered_number ?? '', registered_office: s.registered_office ?? '', address: s.address ?? '',
    contact_email: s.contact_email ?? '', contact_phone: s.contact_phone ?? '', website: s.website ?? '',
    base_currency: s.base_currency, vat_registered: s.vat_registered, vat_number: s.vat_number ?? '', vat_basis: s.vat_basis,
    vat_frequency: s.vat_frequency, vat_period_start_month: s.vat_period_start_month, year_start_month: s.year_start_month,
    first_year: Number(String(s.first_year ?? '').replace(/\D/g, '')) || new Date().getFullYear(),
  };
}

export const BUSINESS_KEYS = ['legal_name', 'trading_name', 'entity_type', 'registered_number', 'registered_office', 'address',
  'contact_email', 'contact_phone', 'website'] as const;
export const VAT_KEYS = ['vat_registered', 'vat_number', 'vat_basis', 'vat_frequency', 'vat_period_start_month'] as const;
export const YEAR_KEYS = ['year_start_month', 'first_year', 'base_currency'] as const;

/** What to send for some fields: blank text goes as null; settings that didn't change aren't sent. */
export function patchFor(s: OrganisationSettings, f: OrgForm, keys: readonly (keyof OrgForm)[]): OrgPatch {
  const before = formFrom(s);
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    if (f[k] === before[k]) continue;
    out[k] = typeof f[k] === 'string' && k !== 'legal_name' && k !== 'base_currency' && k !== 'vat_basis' && k !== 'vat_frequency' && k !== 'entity_type'
      ? ((f[k] as string).trim() || null) : f[k];
  }
  // Registering needs the number to go with it, even if the number didn't change.
  if ('vat_registered' in out && f.vat_registered && !('vat_number' in out)) out.vat_number = f.vat_number.trim() || null;
  return out as OrgPatch;
}

type Props = { s: OrganisationSettings; f: OrgForm; set: (p: Partial<OrgForm>) => void; disabled?: boolean; idp?: string };

const COMPANY_NO = { IE: 'CRO number', UK: 'Companies House number' } as const;

export function BusinessFields({ s, f, set, disabled, idp = 'org' }: Props) {
  const id = (k: string) => `${idp}-${k}`;
  return (
    <div className="el-stack" style={{ gap: 14 }}>
      <div className="el-form-grid">
        <Field id={id('legal')} label="Legal name" hint="As registered, for invoices and returns">
          <input id={id('legal')} className="el-input" value={f.legal_name} disabled={disabled} onChange={(e) => set({ legal_name: e.target.value })} />
        </Field>
        <Field id={id('trading')} label="Trading name" hint="Optional: the name customers know">
          <input id={id('trading')} className="el-input" value={f.trading_name} disabled={disabled} onChange={(e) => set({ trading_name: e.target.value })} />
        </Field>
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={disabled}>
        <legend className="el-label">The business is</legend>
        <div className="el-row" style={{ gap: 16, flexWrap: 'wrap' }}>
          {([['company', 'A limited company'], ['sole_trader', 'A sole trader'], ['partnership', 'A partnership']] as const).map(([v, label]) => (
            <label key={v} className="el-check"><input type="radio" name={id('entity')} checked={f.entity_type === v} onChange={() => set({ entity_type: v })} />{label}</label>
          ))}
        </div>
      </fieldset>
      <div className="el-form-grid">
        {f.entity_type === 'company' && (
          <Field id={id('regno')} label={COMPANY_NO[s.jurisdiction === 'UK' ? 'UK' : 'IE']}>
            <input id={id('regno')} className="el-input" value={f.registered_number} disabled={disabled} onChange={(e) => set({ registered_number: e.target.value })} />
          </Field>
        )}
        <Field id={id('email')} label="Accounts email" hint="Shown on invoices for replies">
          <input id={id('email')} type="email" className="el-input" value={f.contact_email} disabled={disabled} onChange={(e) => set({ contact_email: e.target.value })} />
        </Field>
        <Field id={id('phone')} label="Phone">
          <input id={id('phone')} type="tel" className="el-input" value={f.contact_phone} disabled={disabled} onChange={(e) => set({ contact_phone: e.target.value })} />
        </Field>
        <Field id={id('web')} label="Website">
          <input id={id('web')} className="el-input" value={f.website} disabled={disabled} onChange={(e) => set({ website: e.target.value })} />
        </Field>
      </div>
      {/* Addresses take a column each (a quarter of a desktop screen) rather than stretching across it (#21). */}
      <div className="el-form-grid" data-fill="">
        {f.entity_type === 'company' && (
          <Field id={id('office')} label="Registered office">
            <textarea id={id('office')} className="el-input" rows={4} value={f.registered_office} disabled={disabled} onChange={(e) => set({ registered_office: e.target.value })} />
          </Field>
        )}
        <Field id={id('addr')} label={f.entity_type === 'company' ? 'Trading address' : 'Business address'} hint={f.entity_type === 'company' ? 'If different from the registered office' : undefined}>
          <textarea id={id('addr')} className="el-input" rows={4} value={f.address} disabled={disabled} onChange={(e) => set({ address: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

export function VatFields({ s, f, set, disabled, idp = 'org' }: Props) {
  const id = (k: string) => `${idp}-${k}`;
  const vat = f.vat_registered && f.vat_number.trim() ? checkVatNumber(f.vat_number, s.jurisdiction) : null;
  const n = FREQUENCIES.find((x) => x.value === f.vat_frequency)?.months ?? 2;
  const fits = followsOn(s.last_vat_return_to, f.vat_frequency, f.vat_period_start_month);
  const uk = s.jurisdiction === 'UK';
  return (
    <div className="el-stack" style={{ gap: 14 }}>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={disabled}>
        <legend className="el-label">Registered for VAT?</legend>
        <div className="el-row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <label className="el-check"><input type="radio" name={id('reg')} checked={f.vat_registered} onChange={() => set({ vat_registered: true })} />Yes</label>
          <label className="el-check"><input type="radio" name={id('reg')} checked={!f.vat_registered} onChange={() => set({ vat_registered: false })} />No, not registered</label>
        </div>
      </fieldset>
      {!f.vat_registered && (
        <p className="el-hint" style={{ margin: 0 }}>Invoices won’t charge VAT and the VAT returns page has nothing to file. Bills still record the VAT you paid as part of the cost.</p>
      )}
      {f.vat_registered && (
        <>
          <div className="el-form-grid">
            <Field id={id('vatno')} label="VAT number" error={vat?.error ?? null} hint={vat?.value && vat.value !== f.vat_number ? `Saved as ${vat.value}` : uk ? 'Such as GB123456789' : 'Such as IE1234567T'}>
              <input id={id('vatno')} className="el-input" value={f.vat_number} disabled={disabled} onChange={(e) => set({ vat_number: e.target.value })} />
            </Field>
            <Field id={id('freq')} label="Returns are filed">
              <select id={id('freq')} className="el-input" value={f.vat_frequency} disabled={disabled}
                onChange={(e) => set({ vat_frequency: e.target.value as OrgForm['vat_frequency'] })}>
                {FREQUENCIES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select>
            </Field>
            {n > 1 && (
              <Field id={id('stagger')} label={n === 12 ? 'The VAT year starts in' : 'A cycle starts in'} hint={uk && n === 3 ? 'Your stagger: on HMRC’s VAT certificate' : undefined}>
                <select id={id('stagger')} className="el-input" value={f.vat_period_start_month} disabled={disabled}
                  onChange={(e) => set({ vat_period_start_month: Number(e.target.value) })}>
                  {MONTHS.slice(0, n === 12 ? 12 : n).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
            )}
          </div>
          <p className="el-hint" style={{ margin: 0 }} aria-live="polite">
            {fits ? <>Periods: {periodCycle(f.vat_frequency, f.vat_period_start_month)}.</>
              : <span className="el-field-error">{s.locks.vat_periods} These settings would skip or repeat months.</span>}
          </p>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={disabled}>
            <legend className="el-label">VAT is worked out on</legend>
            <label className="el-check"><input type="radio" name={id('basis')} checked={f.vat_basis === 'invoice'} onChange={() => set({ vat_basis: 'invoice' })} />
              <span>Invoices and bills<span className="el-hint" style={{ display: 'block' }}>When they’re dated: the usual way</span></span></label>
            <label className="el-check"><input type="radio" name={id('basis')} checked={f.vat_basis === 'cash'} onChange={() => set({ vat_basis: 'cash' })} />
              <span>Payments (cash basis)<span className="el-hint" style={{ display: 'block' }}>
                {uk ? 'When money comes in or goes out; for turnover under £1.35 million' : 'Moneys received; for turnover under €2 million, with Revenue’s approval'}</span></span></label>
          </fieldset>
        </>
      )}
    </div>
  );
}

export function YearFields({ s, f, set, disabled, idp = 'org' }: Props) {
  const id = (k: string) => `${idp}-${k}`;
  const yearLocked = Boolean(s.locks.year);
  const ccyLocked = Boolean(s.locks.base_currency);
  const r = firstYearRange(f.year_start_month, f.first_year);
  return (
    <div className="el-stack" style={{ gap: 14 }}>
      <div className="el-form-grid">
        <Field id={id('ysm')} label="The financial year starts in" hint={yearLocked ? s.locks.year : undefined}>
          <select id={id('ysm')} className="el-input" value={f.year_start_month} disabled={disabled || yearLocked}
            onChange={(e) => set({ year_start_month: Number(e.target.value) })}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </Field>
        <Field id={id('fy')} label="First year in these books" hint={`FY${f.first_year} runs ${longDate(r.from)} to ${longDate(r.to)}`}>
          <select id={id('fy')} className="el-input" value={f.first_year} disabled={disabled || yearLocked}
            onChange={(e) => set({ first_year: Number(e.target.value) })}>
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i).concat(f.first_year).filter((y, i, a) => a.indexOf(y) === i).sort()
              .map((y) => <option key={y} value={y}>FY{y}</option>)}
          </select>
        </Field>
        <Field id={id('ccy')} label="The books are kept in" hint={ccyLocked ? s.locks.base_currency : 'Other currencies are converted to this'}>
          <select id={id('ccy')} className="el-input" value={f.base_currency} disabled={disabled || ccyLocked}
            onChange={(e) => set({ base_currency: e.target.value })}>
            {CURRENCIES.filter((c) => ['EUR', 'GBP', 'USD'].includes(c.code) || c.code === f.base_currency)
              .map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
          </select>
        </Field>
      </div>
      {!yearLocked && <p className="el-hint" style={{ margin: 0 }}>Until something is posted, changing these re-cuts the year and its monthly periods.</p>}
    </div>
  );
}
